/**
 * Billing + usage product events (spec 68 §6.6.3, spec 22).
 *
 * AUDIT-KIND RECONCILIATION (gap-68): the spec's §6.6.3 emit site for
 * `billing.spend_cap_breached` names audit kind `spend_cap.breached`, but
 * the code's persist-audit enum (agent-runtime.ts:210) uses
 * `run.budget_blocked`. The event NAME stays `billing.spend_cap_breached`
 * (Amplitude wire name); the pass-2 handler triggers it off the CODE kind
 * `run.budget_blocked` — recorded in BUDGET_BLOCKED_AUDIT_KIND below.
 */
import { z } from 'zod';
import { DEFAULT_SCHEMA_VER, id, microusd } from './_shared.js';

export const billingEvents = {
  'billing.checkout_started': {
    schema: z.object({ account_id: id, target_plan: z.string().min(1) }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'billing.subscription_changed': {
    schema: z.object({
      account_id: id,
      old_plan: z.string().optional(),
      new_plan: z.string().min(1),
      mrr_microusd: microusd.optional(),
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'billing.spend_cap_set': {
    schema: z.object({ account_id: id, cap_microusd: microusd, was_unset: z.boolean().optional() }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'billing.spend_cap_breached': {
    schema: z.object({
      account_id: id,
      attempted_run_id: id,
      cap_microusd: microusd,
      mtd_spend_microusd: microusd,
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'billing.byok_added': {
    schema: z.object({ account_id: id, provider: z.string().min(1) }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'billing.byok_revoked': {
    schema: z.object({ account_id: id, provider: z.string().min(1) }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
} as const;

/** CODE audit kind that triggers `billing.spend_cap_breached` (see above). */
export const BUDGET_BLOCKED_AUDIT_KIND = 'run.budget_blocked' as const;
